import { MutationResult } from "@/api/utils/types"
import {
  IReviewPeriodAdd,
  IReviewPeriodCancel,
  IReviewPeriodEdit,
} from "./ReviewPeriodMutator.types"
import { useMutation } from "@/api/utils/useQuery"
import {
  BackofficeRestMutations,
  CreateReviewPeriodPayload,
  CancelReviewPeriodPayload,
  EditReviewPeriodPayload,
} from "@/api/backofficeRest/mutations"
import { CreateAccountUnderReviewPeriodResponse } from "@/api/backofficeRest/generated/model"
import { useCurrentAccountStore } from "@/stores/currentAccount"
import {
  formatDateDashed,
  formatDateTimeStartOfDayUtc,
} from "@/helpers/dateHelper"
import Cache from "@/api/utils/Cache"

export interface IReviewPeriodMutator {
  addForAccount(): MutationResult<
    IReviewPeriodAdd,
    CreateAccountUnderReviewPeriodResponse
  >
  removeForAccount(): MutationResult<IReviewPeriodCancel, void>
  editForAccount(): MutationResult<IReviewPeriodEdit, void>
}

class ReviewPeriodMutatorClass implements IReviewPeriodMutator {
  addForAccount(): MutationResult<
    IReviewPeriodAdd,
    CreateAccountUnderReviewPeriodResponse
  > {
    return useMutation({
      ...BackofficeRestMutations.reviewPeriods.create(),
      serializerFn: (data: IReviewPeriodAdd): CreateReviewPeriodPayload => {
        const billingAccountId = useCurrentAccountStore().coreId.data
        if (!billingAccountId) throw new Error("Missing coreId")
        return {
          billingAccountId,
          body: {
            fromDttm: formatDateTimeStartOfDayUtc(data.fromDttm),
            toDttm: data.toDttm
              ? formatDateTimeStartOfDayUtc(data.toDttm)
              : undefined,
            reason: data.reason,
            description: data.description,
            suppressBilling: data.isBillingSuppressed,
            suppressDunning: data.isDunningSuppressed,
            suppressPaymentCollection: data.isPaymentCollectionSuppressed,
          },
        }
      },
      onSuccessFn: () => {
        Cache.clear()
      },
    })
  }

  removeForAccount(): MutationResult<IReviewPeriodCancel, void> {
    return useMutation({
      ...BackofficeRestMutations.reviewPeriods.cancel(),
      serializerFn: (data: IReviewPeriodCancel): CancelReviewPeriodPayload => {
        const billingAccountId = useCurrentAccountStore().coreId.data
        if (!billingAccountId) throw new Error("Missing coreId")
        return {
          billingAccountId,
          reviewPeriodId: data.id,
        }
      },
      onSuccessFn: () => {
        Cache.clear()
      },
    })
  }

  editForAccount(): MutationResult<IReviewPeriodEdit, void> {
    return useMutation({
      ...BackofficeRestMutations.reviewPeriods.edit(),
      serializerFn: (data: IReviewPeriodEdit): EditReviewPeriodPayload => {
        const billingAccountId = useCurrentAccountStore().coreId.data
        if (!billingAccountId) throw new Error("Missing coreId")
        if (data.reviewPeriodJuniferId == null) {
          throw new Error("Missing reviewPeriodJuniferId")
        }
        return {
          billingAccountId,
          body: {
            reviewPeriodJuniferId: data.reviewPeriodJuniferId,
            fromDttm: formatDateDashed(data.fromDttm),
            toDttm: data.toDttm ? formatDateDashed(data.toDttm) : undefined,
            reason: data.reason,
            description: data.description,
            suppressBillingFl: data.isBillingSuppressed,
            suppressDunningFl: data.isDunningSuppressed,
            suppressPaymentCollectionFl: data.isPaymentCollectionSuppressed,
          },
        }
      },
    })
  }
}

export const ReviewPeriodMutator = new ReviewPeriodMutatorClass()
